import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { SystemCategoryIdList } from 'constants/documentCategories';
import {
  DOCUMENT_REJECTED_PLACEHOLDER,
  DOCUMENT_REJECTED_STATUS,
  DOCUMENT_UNCATEGORIZED_PLACEHOLDER,
} from 'constants/documents';
import { getConditionalArray } from 'utils/data';
import { findLastCreatedDate, formatFilteredDocumentsByCondition } from 'utils/documents';
import { compareBySearchValue } from 'utils/searchItems';

import { getDocuments } from 'store/documents/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { getDocumentTypes } from './documentTypes.selectors';
import { getSystem } from './system.selectors';

export const getDocumentCategories = createSelector(
  getSystem,
  ({ documentCategories }) => documentCategories,
);

// The store's category list carries the synthetic system categories ("Inactive" = types with no
// real category, "Excluded from data extraction") that the documents tree never renders — for
// tree purposes a type is only "categorized" when a REAL category claims it.
const getNonSystemDocumentCategories = createSelector(getDocumentCategories, categories =>
  (categories || []).filter(
    ({ documentCategoryId }) => !SystemCategoryIdList.includes(documentCategoryId),
  ),
);

const getSortedDocumentCategories = createSelector(getDocumentCategories, documentCategories =>
  documentCategories?.sort((a, b) => a.orderNumber - b.orderNumber),
);

export const getFilteredDocumentCategories = createSelector(
  [getDebouncedSearchValue, getSortedDocumentCategories, getDocumentTypes],
  (searchBundleText, docCategories, docTypes) =>
    docCategories?.reduce((acc, { documentTypeIds, documentCategoryName, ...restProps }) => {
      const matchedDocTypeIds = documentTypeIds.filter(id =>
        compareBySearchValue(docTypes[id]?.documentTypeName, searchBundleText),
      );

      const isDocTypesMatch = !!matchedDocTypeIds.length;
      const isCategoryMatches = compareBySearchValue(documentCategoryName, searchBundleText);

      if (isCategoryMatches || isDocTypesMatch) {
        acc.push({
          ...restProps,
          documentCategoryName,
          documentTypeIds: isDocTypesMatch ? matchedDocTypeIds : documentTypeIds,
        });
      }

      return acc;
    }, []),
);

const selectRejectedPlaceholder = memoizeOne(isDeletedDocumentsDisplayed =>
  createSelector(getDocuments, getDocumentTypes, (documents, documentTypes) => {
    const rejectedDocuments = isDeletedDocumentsDisplayed
      ? formatFilteredDocumentsByCondition(
          documents,
          document => document.ExtractionRequest?.status?.type === DOCUMENT_REJECTED_STATUS,
        )
      : {};

    const rejectedDocumentsIds = _.keys(rejectedDocuments);

    return {
      documentCategoryId: DOCUMENT_REJECTED_PLACEHOLDER,
      documentCategoryName: DOCUMENT_REJECTED_PLACEHOLDER,
      documentTypeIds: rejectedDocumentsIds,
      types: rejectedDocumentsIds.map(id => ({
        id: `${DOCUMENT_REJECTED_PLACEHOLDER}_${id}`,
        name: _.get(documentTypes, [id, 'documentTypeName'], ''),
        types: rejectedDocuments[id] || [],
      })),
    };
  }),
);

// Documents whose type belongs to no category — dropping them silently hid TPO cert doc
// types from the sub-company Documents tab. Mirrors selectRejectedPlaceholder's
// synthetic-node shape so the tree, search, and expand-all treat this bucket like any other.
const selectUncategorizedPlaceholder = memoizeOne(isDeletedDocumentsDisplayed =>
  createSelector(
    getNonSystemDocumentCategories,
    getDocuments,
    getDocumentTypes,
    (categories, documents, documentTypes) => {
      const categorizedDocumentTypeIds = new Set(
        categories.flatMap(({ documentTypeIds }) => documentTypeIds),
      );

      const uncategorizedDocuments = formatFilteredDocumentsByCondition(documents, document => {
        const isRejected = document.ExtractionRequest?.status?.type === DOCUMENT_REJECTED_STATUS;
        const isDisplayable = isDeletedDocumentsDisplayed || !document.isDeleted;

        return (
          !isRejected && isDisplayable && !categorizedDocumentTypeIds.has(document.documentTypeId)
        );
      });

      const uncategorizedDocumentTypeIds = _.keys(uncategorizedDocuments);

      return {
        documentCategoryId: DOCUMENT_UNCATEGORIZED_PLACEHOLDER,
        documentCategoryName: DOCUMENT_UNCATEGORIZED_PLACEHOLDER,
        documentTypeIds: uncategorizedDocumentTypeIds,
        types: uncategorizedDocumentTypeIds.map(id => {
          const types = uncategorizedDocuments[id] || [];
          const type = findLastCreatedDate(types);

          return {
            id,
            name: _.get(documentTypes, [id, 'documentTypeName'], ''),
            types,
            createdDate: type?.createdDate,
          };
        }),
      };
    },
  ),
);

const selectSyntheticPlaceholders = memoizeOne(isDeletedDocumentsDisplayed =>
  createSelector(
    selectRejectedPlaceholder(isDeletedDocumentsDisplayed),
    selectUncategorizedPlaceholder(isDeletedDocumentsDisplayed),
    (rejectedPlaceholder, uncategorizedPlaceholder) => ({
      rejectedPlaceholder,
      uncategorizedPlaceholder,
    }),
  ),
);

export const selectPreviewDocuments = memoizeOne(isDeletedDocumentsDisplayed =>
  createSelector(
    getNonSystemDocumentCategories,
    getDocuments,
    getDocumentTypes,
    selectSyntheticPlaceholders(isDeletedDocumentsDisplayed),
    (categories, documents, documentTypes, { rejectedPlaceholder, uncategorizedPlaceholder }) => {
      if (categories.length === 0) {
        return;
      }

      const preparedDocumentsFilterCondition = document =>
        document.ExtractionRequest?.status?.type !== DOCUMENT_REJECTED_STATUS &&
        (isDeletedDocumentsDisplayed || !document.isDeleted);

      const preparedDocuments = formatFilteredDocumentsByCondition(
        documents,
        preparedDocumentsFilterCondition,
      );

      const preparedCategories = categories.map(category => {
        const { documentTypeIds } = category;
        const types = documentTypeIds.map(id => {
          const types = preparedDocuments[id] || [];
          const type = findLastCreatedDate(types);

          return {
            id,
            name: _.get(documentTypes, [id, 'documentTypeName'], ''),
            types,
            createdDate: type?.createdDate,
          };
        });
        const type = findLastCreatedDate(types);

        return {
          ...category,
          types,
          createdDate: type?.createdDate,
        };
      });

      return [
        ...preparedCategories,
        ...getConditionalArray(!!uncategorizedPlaceholder.documentTypeIds.length, () => [
          uncategorizedPlaceholder,
        ]),
        rejectedPlaceholder,
      ];
    },
  ),
);

export const selectDocumentTypesWithCategories = createSelector(
  [getDocumentCategories, getDocumentTypes],
  (documentCategories, documentTypes) =>
    (documentCategories || []).reduce((acc, category) => {
      if (category.documentTypeIds.length) {
        const mappedTypes = category.documentTypeIds
          .map(typeId =>
            documentTypes[typeId]
              ? {
                  ...documentTypes[typeId],
                  documentCategoryId: category.documentCategoryId,
                  documentCategoryName: category.documentCategoryName,
                }
              : null,
          )
          .filter(Boolean);

        acc.push(...mappedTypes);
      }

      return acc;
    }, []),
);

// Every document type is present here — types not covered by any category map to the
// synthetic Uncategorized bucket so opening a doc of that type still auto-expands its
// container in the tree.
export const getCategoriesGroupedByTypeId = createSelector(
  getNonSystemDocumentCategories,
  getDocumentTypes,
  (documentCategories, documentTypes) => {
    const groupedByTypeId = documentCategories.reduce((acc, category) => {
      category.documentTypeIds.forEach(key => {
        acc[key] = acc[key] || [];
        acc[key].push(category.documentCategoryId);
      });

      return acc;
    }, {});

    Object.keys(documentTypes || {}).forEach(typeId => {
      if (!groupedByTypeId[typeId]) {
        groupedByTypeId[typeId] = [DOCUMENT_UNCATEGORIZED_PLACEHOLDER];
      }
    });

    return groupedByTypeId;
  },
);
